import type { ModalConfigIdType } from 'BreetConfig';
import { type FileType, fileTypeToMimeMap, type KybFormFileType, type PreviewFileMeta } from 'BreetHelpers';
import type { ComponentPropsWithoutRef } from 'react';

export interface PDFViewerProps {
	pdfFile: PreviewFileMeta | string;
	width?: number;
	className?: string;
	// Hides the page navigation controls — used for the inline document thumbnail.
	hideControls?: boolean;
}

export interface MockViewport {
	width: number;
	height: number;
	transform: number[];
}

export interface MockRenderContext {
	canvasContext: CanvasRenderingContext2D;
	viewport: MockViewport;
}

export interface MockRenderTask {
	promise: Promise<void>;
}

export interface MockPage {
	getViewport: (options: { scale: number }) => MockViewport;
	render: (renderContext: MockRenderContext) => MockRenderTask;
}

export interface MockPDF {
	numPages: number;
	getPage: (pageNumber: number) => MockPage;
}

export interface PDFLoadingState {
	isLoading: boolean;
	error: string | null;
}

export const getPreviewFileMeta = (file: FileList | string, fileSelectorId: string): PreviewFileMeta => {
	try {
		const isUrl = typeof file === 'string';
		const type = isUrl ? '' : file[0].type;
		const previewUrl = isUrl ? file : URL.createObjectURL(file[0]);

		return {
			type,
			previewUrl,
			fileSelectorId,
		};
	} catch (error) {
		console.error(error);
		return {
			type: '',
			previewUrl: '',
			fileSelectorId: '',
		};
	}
};

export const getAcceptedMimeTypes = (types?: FileType[]) => {
	if (!types?.length) return '';

	return types
		.map((ext) => fileTypeToMimeMap[ext])
		.filter(Boolean)
		.join(', ');
};

export const getAcceptedFileTypes = (acceptedFileTypes?: FileType[]) => {
	let mimeText = '',
		acceptedMimeTypes = '';

	if (acceptedFileTypes?.length) {
		mimeText = getAcceptedMimeTypes(acceptedFileTypes);

		acceptedMimeTypes = acceptedFileTypes
			.map((fileType) => `.${fileType}`)
			.join(', ')
			.toUpperCase()
			.replace(/\./g, '')
			.replace(/, ([^,]*)$/, ' or $1');
	}

	return {
		mime: mimeText,
		acceptedMimeTypes,
	};
};

export interface IFileInput extends Omit<
	ComponentPropsWithoutRef<'input'>,
	'type' | 'placeholder' | 'accept' | 'value' | 'onChange' | 'id'
> {
	errorMsg?: string;
	label?: string;
	accept?: FileType[];
	value?: FileList | string;
	onChange: (file: FileList | null) => void;
	id: string;
	tooltip?: string;
}

export interface IFileError {
	error: string;
	onReload?: () => void;
}

export interface IFileView {
	value: KybFormFileType;
	acceptedMimeTypes: string;
	onPreview?: () => void;
	onReplace?: () => void;
}

export interface IFilePreviewCTA {
	inputFileSelectorId: string;
	modalId: ModalConfigIdType;
}
